import React from 'react';
import { formatPrice, getStatusConfig, getSiteConfig } from '../../../utils/productUtils';

export const createProductColumns = (selectionHandlers, onDeleteProduct) => {
  const { handleSelectAll, handleSelectProduct, isAllSelected, isIndeterminate, selectedProducts } = selectionHandlers;

  return [
    {
      id: 'select',
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={isAllSelected}
          ref={(el) => {
            if (el) el.indeterminate = isIndeterminate;
          }}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectAll(e.target.checked);
          }}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedProducts.has(row.original.id)}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectProduct(row.original.id, e.target.checked);
          }}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 35,
      minSize: 35,
      maxSize: 35,
    },
    {
      accessorKey: 'media_images',
      header: '이미지',
      cell: ({ getValue, row }) => {
        const media_images = getValue();
        
        // media_images가 있을 때만 이미지 표시
        const imageUrl = media_images && media_images.length > 0 ? media_images[0] : null;
        return (
          <div>
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt={row.original.title}
                className="w-16 h-16 object-cover rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                이미지 없음
              </div>
            )}
          </div>
        );
      },
      size: 90,
      minSize: 85,
      maxSize: 95,
    },
    {
      accessorKey: 'source_site',
      header: '사이트',
      cell: ({ getValue }) => {
        const sourceSite = getValue();
        const siteConfig = getSiteConfig(sourceSite);
        return (
          <span className={siteConfig.className} style={siteConfig.style}>
            {siteConfig.name}
          </span>
        );
      },
      size: 80,
      minSize: 80,
      maxSize: 80,
    },
    {
      accessorKey: 'title',
      header: '제품명',
      cell: ({ getValue, row }) => {
        const title = getValue();
        const titleEn = row.original.title_en;
        return (
          <div>
            <div className="font-bold mb-1 truncate">
              {titleEn}
            </div>
            {title && (
              <div className="text-xs text-gray-600 truncate">
                {title}
              </div>
            )}
          </div>
        );
      },
      size: 250,
      minSize: 50,
      maxSize: 300,
    },
    {
      accessorKey: 'brand_name_en',
      header: '브랜드',
      cell: ({ getValue, row }) => {
        const brandEn = getValue();
        const brandKo = row.original.brand_name;
        return (
          <div>
            <div className="truncate">{brandEn}</div>
            {brandKo && (
              <div className="text-xs text-gray-600 truncate">
                {brandKo}
              </div>
            )}
          </div>
        );
      },
      size: 110,
      minSize: 100,
      maxSize: 120,
    },
    {
      accessorKey: 'price',
      header: '가격',
      cell: ({ getValue, row }) => {
        const price = getValue();
        const priceUsd = row.original.price_usd;
        return (
          <div>
            {priceUsd && 
              <div className="font-bold mb-1 truncate">
                ${priceUsd}
              </div>
            }
            {price && (
              <div className="text-xs text-gray-600 truncate">
                {formatPrice(price)}
              </div>
            )}
          </div>
        )
      },
      size: 85,
      minSize: 80,
      maxSize: 100,
    },
    // {
    //   accessorKey: 'sale_status',
    //   header: '상태',
    //   cell: ({ getValue }) => {
    //     const status = getValue();
    //     const config = getStatusConfig(status);
        
    //     return (
    //       <div className="flex justify-center">
    //         <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${config.className}`}>
    //           {config.text}
    //         </span>
    //       </div>
    //     );
    //   },
    //   size: 75,
    //   minSize: 70,
    //   maxSize: 85,
    // },
    {
      accessorKey: 'category',
      header: '카테고리',
      cell: ({ getValue, row }) => {
        const productType = row.original.product_type;
        const category = getValue();
        const categoryEn = row.original.category_en;
        return (
          <div>
            {productType && (
              <div className="font-medium text-gray-900 truncate">{productType}</div>
            )}
            <div className="mt-1 space-y-1">
              {categoryEn && (
                <div className="text-xs text-gray-500 block truncate">
                  {categoryEn}
                </div>
              )}
              <div className="text-xs text-gray-600 block truncate">
                {category || '-'}
              </div>
            </div>
          </div>
        );
      },
      size: 100,
      minSize: 90,
      maxSize: 110,
    },
    {
      accessorKey: 'crawled_at',
      header: '일시',
      meta: {
        className: 'hidden xl:table-cell'
      },
      cell: ({ getValue }) => {
        const dateString = getValue();
        if (!dateString) return '-';
        return (
          <span className="text-xs text-gray-600">
            {new Date(dateString).toLocaleString('ko-KR', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '삭제',
      cell: ({ row }) => (
        <div className="flex gap-2 flex justify-center" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                if (onDeleteProduct) {
                  onDeleteProduct(row.original.id);
                }
              }}
              className="bg-gray-400 text-gray-200 hover:bg-red-700 hover:text-white px-1 rounded text-xs transition-colors whitespace-nowrap"
            >
              x
            </button>
        </div>
      ),
      enableSorting: false,
      size: 60,
      minSize: 60,
      maxSize: 60,
    },
  ];
};
